import * as preact from "preact";
import * as _ from "underscore";

import * as localAPI from "localAPI";
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient, { requestErrorMessage } from "readlangClient";
import {
  dialogueCategory,
  normalizeHeadingLevels,
  stripSeparators,
} from "shared/bookUtils";
import * as languageMap from "shared/languageMap";
import * as signupModal from "signupModal";
import stores from "stores/stores";
import Button from "ui/Button";
import LanguageSelectorView from "views/LanguageSelectorView";

type Props = {
  appRouter: any;
};

type State = {
  genreType:
    | "classic-fiction"
    | "non-fiction"
    | "biography"
    | "original-fiction"
    | "dialogue";
  topic: string;
  level: "beginner" | "intermediate" | "advanced";
  length: number;

  showLanguageSelector?: boolean;

  generating: boolean;
  generatedStory?: string;
  generatingComplete: boolean;
};

const genres: {
  type: State["genreType"];
  name: string;
  topicName: string;
  topicDescription: string;
  topicPlaceholder: string;
}[] = [
  {
    type: "classic-fiction",
    name: "Classic Fiction",
    topicName: "Name of Story",
    topicDescription:
      "Enter the name of a widely known classic story or fairy tale, e.g. The Three Little Pigs, Oliver Twist, The Iliad, etc...",
    topicPlaceholder: "Type name of story",
  },
  {
    type: "non-fiction",
    name: "Non-Fiction",
    topicName: "Topic",
    topicDescription:
      "Enter a widely known non-fiction topic to simplify, e.g. The History of the Internet, World War 2, Particle Physics, etc...",
    topicPlaceholder: "Type topic",
  },
  {
    type: "biography",
    name: "Biography",
    topicName: "Person",
    topicDescription:
      "Enter the name of a widely known person whose story you want to write, e.g. Albert Einstein, Marie Curie, Simón Bolívar etc...",
    topicPlaceholder: "Type name of person",
  },
  {
    type: "original-fiction",
    name: "Original Fiction",
    topicName: "Idea",
    topicDescription:
      "Enter an idea for the story you want to write, e.g. a haunted house, a love story on a spaceship, etc...",
    topicPlaceholder: "Type idea",
  },
  {
    type: "dialogue",
    name: "Dialogue",
    topicName: "Scenario",
    topicDescription:
      "Enter a scenario for the dialogue, e.g. ordering food in a restaurant, a job interview, two friends meeting after years, etc...",
    topicPlaceholder: "Type scenario",
  },
];

const genresMap = _.indexBy(genres, "type");

class StoryBotPageView extends preact.Component<Props, State> {
  languageSelectorElement: HTMLElement;
  private disposeUserChange?: () => void;

  constructor(props: Props) {
    super(props);

    this.state = {
      genreType: "classic-fiction",
      topic: "",
      level: "beginner",
      length: 1000,

      generatingComplete: false,
      generating: false,
    };

    this.disposeUserChange = stores.user.onChange(
      (user) => [
        user.currentLearningLanguage,
        user.activeLanguages,
        user.subscriptionPlan,
      ],
      this.handleUserChange
    );

    // Re-fetch user to ensure we have up-to-date data
    stores.user.fetch();
    document.addEventListener("click", this.onClickOutsideLanguageSelector);
  }

  handleUserChange = () => {
    this.forceUpdate();
  };

  onClickOutsideLanguageSelector = (event: MouseEvent) => {
    if (
      this.state.showLanguageSelector &&
      !event.composedPath().includes(this.languageSelectorElement)
    ) {
      this.setState({ showLanguageSelector: false });
    }
  };

  componentWillUnmount() {
    this.disposeUserChange?.();
    document.removeEventListener("click", this.onClickOutsideLanguageSelector);
  }

  render() {
    const languageString =
      languageMap.map[stores.user.currentLearningLanguage.value].english;

    const genre = genres.find(
      (potentialGenre) => potentialGenre.type === this.state.genreType
    );

    return (
      <div class="storyBotContainer">
        <h2>Readlang Story Bot</h2>

        <img class="storyBotImage" src="/content/storyBot.webp" />

        <div class="topSection">
          <p>
            Ask the Readlang Story Bot to write you a story or article to
            practice reading {languageString}.
          </p>
          <p>
            All generated texts will be shared on the{" "}
            <a href="/library">{languageString} public library</a>.
          </p>
        </div>

        <div class="storyBotForm">
          <div class="field languageSelectorContainer">
            <span class="fieldLabel">Language</span>
            {this.state.generating ? (
              <select disabled>
                <option>{languageString}</option>
              </select>
            ) : (
              <>
                <span
                  onClick={(event) => {
                    // Stop propagation so that it doesn't trigger the click handler on the whole
                    // document used to dismiss the dropdown
                    event.stopPropagation();
                    this.setState({ showLanguageSelector: true });
                  }}
                >
                  <select class="languageSelect">
                    <option>{languageString}</option>
                  </select>
                </span>
                {this.state.showLanguageSelector ? (
                  <div
                    class="languageSelectorPopup"
                    ref={(element) => {
                      this.languageSelectorElement = element;
                    }}
                  >
                    <h3>Select language</h3>
                    <LanguageSelectorView
                      featuredLanguages={
                        stores.user.state.value.activeLanguages
                      }
                      languageType="learning"
                      setLanguage={(languageCode: string) => {
                        this.setState({ showLanguageSelector: false });
                        stores.user.save({
                          currentLearningLanguage: languageCode,
                        });
                      }}
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>

          <label class="field">
            <span class="fieldLabel">Genre</span>
            <select
              value={this.state.genreType}
              onChange={(event) => {
                this.setState({
                  genreType: event.currentTarget.value as State["genreType"],
                });
              }}
              disabled={this.state.generating}
            >
              {genres.map((thisGenre) => (
                <option value={thisGenre.type}>{thisGenre.name}</option>
              ))}
            </select>
          </label>

          <label class="field">
            <span class="fieldLabel">{genre.topicName}</span>
            <input
              type="text"
              value={this.state.topic}
              placeholder={genre.topicPlaceholder}
              onInput={(event) =>
                this.setState({ topic: event.currentTarget.value })
              }
              disabled={this.state.generating}
            />
            <span class="fieldHint">{genre.topicDescription}</span>
          </label>

          <div class="fieldPair">
            <label class="field">
              <span class="fieldLabel">Reading Level</span>
              <select
                value={this.state.level}
                onChange={(event) =>
                  this.setState({
                    level: event.currentTarget.value as State["level"],
                  })
                }
                disabled={this.state.generating}
              >
                <option value="beginner">Beginner</option>
                <option value="intermetiate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </label>

            <label class="field">
              <span class="fieldLabel">Length (approx.)</span>
              <select
                value={this.state.length}
                onChange={(event) =>
                  this.setState({
                    length: parseInt(event.currentTarget.value, 10),
                  })
                }
                disabled={this.state.generating}
              >
                <option value={300}>300 words</option>
                {/* Intentionally displaying a different number to the user since gpt-4o is
                 consistently writing stories with fewer than 1000 words. */}
                <option value={1000}>800 words</option>
              </select>
            </label>
          </div>

          {this.state.generating ? (
            <p class="generatingStatus">
              {this.state.generatedStory
                ? `Generated ${this.state.generatedStory.split(" ").length} words`
                : `Generating...`}
            </p>
          ) : (
            <Button
              kind="primary"
              size="large"
              block
              class="generateButton"
              onClick={() => this.generateStory()}
            >
              Generate Story
            </Button>
          )}
        </div>

        <p>
          Visit{" "}
          <a href="https://forum.readlang.com/t/new-story-bot-for-ai-generated-stories/1309">
            this forum thread
          </a>{" "}
          to leave feedback about the Readlang Story Bot.
        </p>
      </div>
    );
  }

  generateStory = async () => {
    if (await localAPI.isLocalMode()) {
      console.log("local api");
      signupModal.show({
        heading: "Sign in and upgrade to use Story Bot",
        stores: stores,
      });
      return;
    }
    if (!stores.user.hasPremiumAccess.value) {
      premiumUpsellModal.show({
        title: "storyBot",
        heading: "Upgrade to use the Story Bot",
        showTitle: false,
      });
      return;
    }

    if (this.state.topic.trim() === "") {
      alert(`Please enter ${genresMap[this.state.genreType].topicName}`);
      return;
    }

    // Clear any previous attempt's output so a retry after a failure doesn't
    // append the new stream to the old story and upload the concatenation.
    // (The story text is never rendered — only a word count — so nothing
    // visible is lost by regenerating from scratch.)
    this.setState({
      generating: true,
      generatedStory: undefined,
      generatingComplete: false,
    });

    fetch(
      `/api/storyBot/generate?genre=${encodeURIComponent(
        this.state.genreType
      )}&topic=${encodeURIComponent(
        this.state.topic.trim()
      )}&length=${encodeURIComponent(
        this.state.length
      )}&level=${encodeURIComponent(
        this.state.level
      )}&language=${encodeURIComponent(stores.user.currentLearningLanguage.value)}`,
      {
        method: "GET",
      }
    )
      .then(async (response) => {
        if (response?.ok === false) {
          // Re-enable the generate controls on every failure branch below
          this.setState({ generating: false });
          if (response.status === 403) {
            // To be sure that the user has reached the limit, we could also check the response
            // text, but for simplicity we're going to assume that a 403 here means that they've
            // hit the daily limit.
            alert("Not permitted");
          }
          const text = await response.text();
          if (response.status === 400 && text === "bad-topic") {
            switch (this.state.genreType) {
              case "classic-fiction":
                alert(
                  `Readlang Story Bot does not recognize the story "${this.state.topic}"`
                );
                return;

              case "original-fiction":
                alert(
                  `Readlang Story Bot refuses to write a story about "${this.state.topic}"`
                );
                return;

              case "non-fiction":
                alert(
                  `Readlang Story Bot doesn't know enough to write about "${this.state.topic}"`
                );
                return;

              case "biography":
                alert(
                  `Readlang Story Bot is not sure who "${this.state.topic}" is`
                );
                return;

              case "dialogue":
                alert(
                  `Readlang Story Bot refuses to write a dialogue about "${this.state.topic}"`
                );
                return;
            }
          }
          alert("Bad request: " + response.status + " " + text);
          return;
        }

        const reader = response.body.getReader();
        const processStream = () =>
          reader.read().then(async ({ done, value }) => {
            if (done) {
              this.setState({ generatingComplete: true });

              const { data } = await readlangClient.request(
                "GET",
                "/api/storyBot/title",
                {
                  genre: this.state.genreType,
                  topic: this.state.topic,
                  snippet: this.state.generatedStory.substring(0, 300),
                  language: stores.user.currentLearningLanguage.value,
                }
              );

              // Upload book to library (awaited so a failure reaches the
              // catch below instead of silently losing the story)
              await readlangClient.request(
                "POST",
                "/api/book",
                {
                  title: data.title,
                  author: "Readlang Story Bot",
                  category: (() => {
                    switch (this.state.genreType) {
                      case "classic-fiction":
                      case "original-fiction":
                        return "Fiction";

                      case "dialogue":
                        return dialogueCategory;

                      case "non-fiction":
                      case "biography":
                        return "Non-Fiction";
                    }
                  })(),
                  language: stores.user.currentLearningLanguage.value,
                  useEveryNewLine: true,
                  public: true,
                  source: "storyBot",
                  body: normalizeHeadingLevels(
                    stripSeparators(this.state.generatedStory)
                  ),
                },
                (responseData) => {
                  this.props.appRouter.navigate(
                    `/library/${responseData.bookID}`,
                    { trigger: true }
                  );
                }
              );

              return;
            }

            // Decode the received bytes and update the UI
            const decoded = new TextDecoder("utf-8").decode(value);
            if (decoded !== undefined) {
              await this.setState({
                generatedStory: (this.state.generatedStory ?? "") + decoded,
              });
            }

            // Continue processing the stream
            return processStream();
          });

        return processStream();
      })
      .catch((error) => {
        this.setState({ generating: false });
        // Terminal request failures arrive already notified by readlangClient
        if (!error?.jqXHR?.alreadyNotified) {
          alert("ERROR: " + requestErrorMessage(error));
        }
      });
  };
}

export default StoryBotPageView;
